import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiPlus, FiTrash2, FiSave, FiX, FiChevronDown, FiSearch, FiTruck, FiEdit2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { purchaseBillsAPI, productsAPI, suppliersAPI } from '../../services/api';
import AddSupplierModal from '../../components/common/AddSupplierModal';

const PurchaseBillForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  // Form state
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);

  // Supplier state
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [showEditSupplierModal, setShowEditSupplierModal] = useState(false);
  const [supplierToEdit, setSupplierToEdit] = useState(null);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const supplierDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    bill_number: '',
    bill_date: new Date().toISOString().split('T')[0],
    supplier_name: '',
    supplier_email: '',
    supplier_phone: '',
    supplier_address: '',
    supplier_gstin: '',
    payment_status: 'unpaid',
    payment_mode: '',
    delivery_status: 'pending',
    paid_amount: 0,
    notes: '',
    terms: 'Payment due within 30 days.\nThank you for your business!',
    delivery_address: '',
    delivery_city: '',
    delivery_state: '',
    delivery_pincode: ''
  });

  const [items, setItems] = useState([
    {
      product_id: '',
      item_name: '',
      description: '',
      hsn_sac: '',
      quantity: 1,
      unit: 'PCS',
      rate: 0,
      tax_rate: 18,
      discount: 0,
      tax_amount: 0,
      amount: 0
    }
  ]);

  const [totals, setTotals] = useState({
    subtotal: 0,
    total_discount: 0,
    total_tax: 0,
    grand_total: 0,
    due_amount: 0
  });

  useEffect(() => {
    fetchProducts();
    fetchSuppliers();
    if (isEditMode) {
      fetchPurchaseBill();
    }
  }, [id]);

  useEffect(() => {
    calculateTotals();
  }, [items, formData.paid_amount]);

  // Handle click outside supplier dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target)) {
        setShowSupplierDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getAll({ limit: 1000, status: 'active' });
      setProducts(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      setSuppliersLoading(true);
      const response = await suppliersAPI.getAllForDropdown();
      setSuppliers(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      // Suppliers table might not exist yet - that's okay, user can still enter supplier details manually
      setSuppliers([]);
    } finally {
      setSuppliersLoading(false);
    }
  };

  // Handle supplier selection
  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch(supplier.name + (supplier.company_name ? ` (${supplier.company_name})` : ''));
    setShowSupplierDropdown(false);

    // Auto-fill supplier fields
    setFormData(prev => ({
      ...prev,
      supplier_name: supplier.name,
      supplier_email: supplier.email || '',
      supplier_phone: supplier.phone || '',
      supplier_gstin: supplier.gst_number || '',
      supplier_address: [
        supplier.address,
        supplier.city,
        supplier.state,
        supplier.pincode
      ].filter(Boolean).join(', ')
    }));
  };

  // Handle new supplier saved from modal
  const handleSupplierSaved = (newSupplier) => {
    // Add to suppliers list
    setSuppliers(prev => [newSupplier, ...prev]);
    // Auto-select the new supplier
    handleSupplierSelect(newSupplier);
    setShowAddSupplierModal(false);
  };

  // Handle supplier updated from edit modal
  const handleSupplierUpdated = (updatedSupplier) => {
    // Update the supplier in the suppliers list
    setSuppliers(prev =>
      prev.map(s => s.id === updatedSupplier.id ? updatedSupplier : s)
    );
    // Update the selected supplier and form data
    handleSupplierSelect(updatedSupplier);
    setShowEditSupplierModal(false);
    setSupplierToEdit(null);
  };

  // Handle edit supplier button click
  const handleEditSupplier = () => {
    if (selectedSupplier) {
      setSupplierToEdit(selectedSupplier);
      setShowEditSupplierModal(true);
    }
  };

  // Filter suppliers based on search
  const filteredSuppliers = suppliers.filter(supplier => {
    const search = supplierSearch.toLowerCase();
    return (
      supplier.name?.toLowerCase().includes(search) ||
      supplier.company_name?.toLowerCase().includes(search) ||
      supplier.phone?.includes(search) ||
      supplier.gst_number?.toLowerCase().includes(search)
    );
  });

  const fetchPurchaseBill = async () => {
    try {
      setLoading(true);
      const response = await purchaseBillsAPI.getById(id);
      const bill = response.data.data;

      // Format date for input field (YYYY-MM-DD)
      const formattedDate = bill.bill_date
        ? new Date(bill.bill_date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];

      setFormData({
        bill_number: bill.bill_number || '',
        bill_date: formattedDate,
        supplier_name: bill.supplier_name || '',
        supplier_email: bill.supplier_email || '',
        supplier_phone: bill.supplier_contact || '',
        supplier_address: bill.supplier_address || '',
        supplier_gstin: bill.supplier_gstin || '',
        payment_status: bill.payment_status,
        payment_mode: bill.payment_mode || '',
        delivery_status: bill.delivery_status || 'pending',
        paid_amount: parseFloat(bill.paid_amount || 0),
        notes: bill.notes || '',
        terms: bill.terms || '',
        delivery_address: bill.delivery_address || '',
        delivery_city: bill.delivery_city || '',
        delivery_state: bill.delivery_state || '',
        delivery_pincode: bill.delivery_pincode || ''
      });

      // Set selected supplier from bill data
      if (bill.supplier_id) {
        const supplierData = {
          id: bill.supplier_id,
          name: bill.supplier_display_name || bill.supplier_name,
          company_name: bill.supplier_company || '',
          email: bill.supplier_email,
          phone: bill.supplier_contact,
          gst_number: bill.supplier_gstin,
          address: bill.supplier_address,
          city: bill.supplier_city || '',
          state: bill.supplier_state || '',
          pincode: bill.supplier_pincode || ''
        };
        setSelectedSupplier(supplierData);
        setSupplierSearch(supplierData.name + (supplierData.company_name ? ` (${supplierData.company_name})` : ''));
      }

      if (bill.items && bill.items.length > 0) {
        setItems(bill.items.map(item => ({
          product_id: item.product_id || '',
          item_name: item.item_name || item.product_name || '',
          description: item.description || '',
          hsn_sac: item.hsn_sac || '',
          quantity: parseFloat(item.quantity),
          unit: item.unit || item.product_unit || 'PCS',
          rate: parseFloat(item.rate),
          tax_rate: parseFloat(item.tax_rate || 0),
          discount: parseFloat(item.discount || 0),
          tax_amount: parseFloat(item.tax_amount || 0),
          amount: parseFloat(item.amount)
        })));
      }
    } catch (error) {
      toast.error('Failed to fetch purchase bill');
      navigate('/purchase-bills');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    // Auto-fill product details when product is selected
    if (field === 'product_id' && value) {
      const product = products.find(p => p.id === parseInt(value));
      if (product) {
        newItems[index].item_name = product.name;
        newItems[index].description = product.description || '';
        newItems[index].unit = product.unit;
        newItems[index].rate = parseFloat(product.price);
        newItems[index].tax_rate = parseFloat(product.tax_rate);
      }
    }

    // Calculate item amount
    if (['quantity', 'rate', 'tax_rate', 'discount'].includes(field)) {
      const item = newItems[index];
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      const taxRate = parseFloat(item.tax_rate) || 0;
      const discount = parseFloat(item.discount) || 0;

      const baseAmount = qty * rate;
      const discountAmount = (baseAmount * discount) / 100;
      const taxableAmount = baseAmount - discountAmount;
      const taxAmount = (taxableAmount * taxRate) / 100;

      newItems[index].tax_amount = taxAmount;
      newItems[index].amount = taxableAmount + taxAmount;
    }

    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        product_id: '',
        item_name: '',
        description: '',
        hsn_sac: '',
        quantity: 1,
        unit: 'PCS',
        rate: 0,
        tax_rate: 18,
        discount: 0,
        tax_amount: 0,
        amount: 0
      }
    ]);
  };

  const removeItem = (index) => {
    if (items.length === 1) {
      toast.error('At least one item is required');
      return;
    }
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      return sum + (qty * rate);
    }, 0);

    const total_discount = items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      const discount = parseFloat(item.discount) || 0;
      return sum + ((qty * rate * discount) / 100);
    }, 0);

    const total_tax = items.reduce((sum, item) => {
      return sum + (parseFloat(item.tax_amount) || 0);
    }, 0);

    const grand_total = items.reduce((sum, item) => {
      return sum + (parseFloat(item.amount) || 0);
    }, 0);

    const paid_amount = parseFloat(formData.paid_amount) || 0;
    const due_amount = grand_total - paid_amount;

    setTotals({
      subtotal,
      total_discount,
      total_tax,
      grand_total,
      due_amount
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation - Bill number is required
    if (!formData.bill_number.trim()) {
      toast.error('Please enter a bill number');
      return;
    }

    // Validation - Either supplier selected OR supplier name entered manually
    const supplierName = selectedSupplier ? selectedSupplier.name : formData.supplier_name.trim();
    if (!supplierName) {
      toast.error('Please select a supplier or enter supplier name');
      return;
    }

    if (items.some(item => !item.item_name.trim())) {
      toast.error('All items must have a name');
      return;
    }

    if (items.some(item => parseFloat(item.quantity) <= 0)) {
      toast.error('All items must have quantity greater than 0');
      return;
    }

    try {
      setLoading(true);

      const paidAmount = Number(formData.paid_amount) || 0;

      // In edit mode, use user's selection; in create mode, calculate from paid amount
      let payment_status;
      if (isEditMode) {
        payment_status = formData.payment_status;
      } else {
        payment_status = 'unpaid';
        if (paidAmount > 0 && paidAmount < totals.grand_total) {
          payment_status = 'partial';
        } else if (paidAmount >= totals.grand_total) {
          payment_status = 'paid';
        }
      }

      // Build purchase bill payload with supplier_id (if available)
      const billData = {
        bill_number: formData.bill_number.trim(),
        bill_date: new Date(formData.bill_date).toISOString(),
        supplier_id: selectedSupplier ? selectedSupplier.id : null,
        supplier_name: supplierName,
        supplier_contact: formData.supplier_phone || null,
        supplier_email: formData.supplier_email || null,
        supplier_address: formData.supplier_address || null,
        supplier_gstin: formData.supplier_gstin || null,

        items: items.map(item => ({
          product_id: item.product_id ? Number(item.product_id) : null,
          name: item.item_name,
          description: item.description || null,
          hsn_sac: item.hsn_sac || null,
          unit: item.unit,
          quantity: Number(item.quantity),
          rate: Number(item.rate),
          discount: Number(item.discount || 0),
          tax_rate: Number(item.tax_rate || 0),
          tax_amount: Number(item.tax_amount || 0),
          total: Number(item.amount)
        })),

        subtotal: Number(totals.subtotal),
        discount_amount: Number(totals.total_discount),
        tax_amount: Number(totals.total_tax),
        total_amount: Number(totals.grand_total),
        paid_amount: paidAmount,
        payment_status,

        payment_mode: formData.payment_mode || null,
        delivery_status: formData.delivery_status || 'pending',

        notes: formData.notes || null,
        terms: formData.terms || null,

        delivery_address: formData.delivery_address || null,
        delivery_city: formData.delivery_city || null,
        delivery_state: formData.delivery_state || null,
        delivery_pincode: formData.delivery_pincode || null
      };

      if (isEditMode) {
        await purchaseBillsAPI.update(id, billData);
        toast.success('Purchase bill updated successfully');
      } else {
        await purchaseBillsAPI.create(billData);
        toast.success('Purchase bill created successfully');
      }

      navigate('/purchase-bills');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save purchase bill');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="p-6 flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {isEditMode ? 'Edit Purchase Bill' : 'Create Purchase Bill'}
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            {isEditMode ? 'Update purchase bill details' : 'Fill in the details to create a new purchase bill'}
          </p>
        </div>
        <button
          onClick={() => navigate('/purchase-bills')}
          className="text-gray-600 hover:text-gray-800"
        >
          <FiX size={24} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Bill Details */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Bill Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bill Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="bill_number"
                value={formData.bill_number}
                onChange={handleInputChange}
                placeholder="Enter bill number"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bill Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="bill_date"
                value={formData.bill_date}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Delivery Address subsection */}
          <div className="mt-6">
            <h3 className="text-md font-medium text-gray-700 mb-3 border-b pb-2">Delivery Address</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  name="delivery_address"
                  value={formData.delivery_address}
                  onChange={handleInputChange}
                  placeholder="Enter delivery address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  name="delivery_city"
                  value={formData.delivery_city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  maxLength="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  name="delivery_state"
                  value={formData.delivery_state}
                  onChange={handleInputChange}
                  placeholder="Enter state"
                  maxLength="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pincode
                </label>
                <input
                  type="text"
                  name="delivery_pincode"
                  value={formData.delivery_pincode}
                  onChange={handleInputChange}
                  placeholder="Enter pincode"
                  maxLength="6"
                  pattern="[0-9]{6}"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Supplier Details */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Supplier Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Supplier Selection Dropdown */}
            <div className="md:col-span-2" ref={supplierDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Supplier <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={supplierSearch}
                    onChange={(e) => {
                      setSupplierSearch(e.target.value);
                      setShowSupplierDropdown(true);
                      // Clear selection if user modifies the text
                      if (selectedSupplier && e.target.value !== (selectedSupplier.name + (selectedSupplier.company_name ? ` (${selectedSupplier.company_name})` : ''))) {
                        setSelectedSupplier(null);
                      }
                    }}
                    onFocus={() => setShowSupplierDropdown(true)}
                    placeholder="Search supplier by name, phone, or GSTIN..."
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <FiChevronDown
                    className={`absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-transform ${showSupplierDropdown ? 'rotate-180' : ''}`}
                    size={18}
                  />
                </div>

                {/* Dropdown List */}
                {showSupplierDropdown && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {/* Add New Supplier Option */}
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddSupplierModal(true);
                        setShowSupplierDropdown(false);
                      }}
                      className="w-full px-4 py-3 text-left flex items-center gap-3 bg-blue-50 hover:bg-blue-100 text-blue-700 border-b border-gray-200 transition-colors"
                    >
                      <FiTruck size={18} />
                      <span className="font-medium">Add New Supplier</span>
                    </button>

                    {suppliersLoading ? (
                      <div className="px-4 py-3 text-gray-500 text-center">
                        Loading suppliers...
                      </div>
                    ) : filteredSuppliers.length === 0 ? (
                      <div className="px-4 py-3 text-gray-500 text-center">
                        No suppliers found
                      </div>
                    ) : (
                      filteredSuppliers.slice(0, 50).map(supplier => (
                        <button
                          key={supplier.id}
                          type="button"
                          onClick={() => handleSupplierSelect(supplier)}
                          className={`w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors ${selectedSupplier?.id === supplier.id ? 'bg-blue-50' : ''
                            }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium text-gray-900">{supplier.name}</div>
                              {supplier.company_name && (
                                <div className="text-sm text-gray-500">{supplier.company_name}</div>
                              )}
                            </div>
                            {supplier.gst_number && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {supplier.gst_number}
                              </span>
                            )}
                          </div>
                          {supplier.phone && (
                            <div className="text-sm text-gray-500 mt-1">{supplier.phone}</div>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Selected Supplier Details Display */}
            {selectedSupplier && (
              <div className="md:col-span-2 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-blue-800 text-lg">{selectedSupplier.name}</h3>
                    {selectedSupplier.company_name && (
                      <p className="text-blue-700">{selectedSupplier.company_name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleEditSupplier}
                      className="text-orange-600 hover:text-orange-800 text-sm flex items-center gap-1"
                      title="Edit supplier details"
                    >
                      <FiEdit2 size={14} />
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedSupplier(null);
                        setSupplierSearch('');
                        setFormData(prev => ({
                          ...prev,
                          supplier_name: '',
                          supplier_email: '',
                          supplier_phone: '',
                          supplier_gstin: '',
                          supplier_address: ''
                        }));
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      Change
                    </button>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-700">
                  {selectedSupplier.phone && (
                    <p><span className="font-medium">Phone:</span> {selectedSupplier.phone}</p>
                  )}
                  {selectedSupplier.email && (
                    <p><span className="font-medium">Email:</span> {selectedSupplier.email}</p>
                  )}
                  {selectedSupplier.gst_number && (
                    <p><span className="font-medium">GSTIN:</span> {selectedSupplier.gst_number}</p>
                  )}
                  {(selectedSupplier.address || selectedSupplier.city || selectedSupplier.state) && (
                    <p className="md:col-span-2">
                      <span className="font-medium">Address:</span>{' '}
                      {[selectedSupplier.address, selectedSupplier.city, selectedSupplier.state, selectedSupplier.pincode]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Manual Supplier Entry (when no supplier selected) */}
            {/* {!selectedSupplier && (
              <div className="md:col-span-2 mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Or enter supplier details manually:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Supplier Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="supplier_name"
                      value={formData.supplier_name}
                      onChange={handleInputChange}
                      placeholder="Enter supplier name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="text"
                      name="supplier_phone"
                      value={formData.supplier_phone}
                      onChange={handleInputChange}
                      placeholder="Enter phone number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="supplier_email"
                      value={formData.supplier_email}
                      onChange={handleInputChange}
                      placeholder="Enter email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GSTIN
                    </label>
                    <input
                      type="text"
                      name="supplier_gstin"
                      value={formData.supplier_gstin}
                      onChange={handleInputChange}
                      placeholder="Enter GSTIN"
                      maxLength={15}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <textarea
                      name="supplier_address"
                      value={formData.supplier_address}
                      onChange={handleInputChange}
                      rows="2"
                      placeholder="Enter full address"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            )} */}
          </div>
        </div>

        {/* Bill Items */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Bill Items</h2>
            <button
              type="button"
              onClick={addItem}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <FiPlus size={16} />
              Add Item
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">HSN/SAC</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Qty</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Unit</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Rate</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Tax%</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Disc%</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Amount</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-16">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((item, index) => (
                  <tr key={index}>
                    <td className="px-3 py-2">
                      <select
                        value={item.product_id}
                        onChange={(e) => handleItemChange(index, 'product_id', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Custom Item</option>
                        {products.map(product => (
                          <option key={product.id} value={product.id}>
                            {product.name} ({product.item_code})
                          </option>
                        ))}
                      </select>
                      {!item.product_id && (
                        <input
                          type="text"
                          value={item.item_name}
                          onChange={(e) => handleItemChange(index, 'item_name', e.target.value)}
                          placeholder="Item name"
                          required
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm mt-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder="Description"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={item.hsn_sac}
                        onChange={(e) => handleItemChange(index, 'hsn_sac', e.target.value)}
                        placeholder=""
                        maxLength="20"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        min="0.01"
                        step="0.01"
                        required
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={item.unit}
                        onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                        placeholder="PCS"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                        min="0"
                        step="0.01"
                        required
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={item.tax_rate}
                        onChange={(e) => handleItemChange(index, 'tax_rate', e.target.value)}
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={item.discount}
                        onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                        min="0"
                        max="100"
                        step="0.01"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm font-medium text-gray-900">
                        ₹{item.amount.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-600 hover:text-red-800"
                        title="Remove item"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-6 flex justify-end">
            <div className="w-full md:w-1/3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">₹{totals.subtotal.toFixed(2)}</span>
              </div>
              {totals.total_discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Discount:</span>
                  <span className="font-medium text-red-600">-₹{totals.total_discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax:</span>
                <span className="font-medium">₹{totals.total_tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Grand Total:</span>
                <span className="text-blue-600">₹{totals.grand_total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Payment Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paid Amount
              </label>
              <input
                type="number"
                name="paid_amount"
                value={formData.paid_amount}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                max={totals.grand_total}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Mode
              </label>
              <select
                name="payment_mode"
                value={formData.payment_mode}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select mode</option>
                <option value="cash">Cash</option>
                <option value="online">Online Transfer</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="card">Card</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Status
              </label>
              <select
                name="payment_status"
                value={formData.payment_status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
              </select>
            </div>

            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delivery Status
              </label>
              <select
                name="delivery_status"
                value={formData.delivery_status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="dispatched">Dispatched</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div> */}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Amount
              </label>
              <input
                type="text"
                value={`₹${totals.due_amount.toFixed(2)}`}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 font-medium"
              />
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Additional Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="4"
                placeholder="Any additional notes..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Terms & Conditions
              </label>
              <textarea
                name="terms"
                value={formData.terms}
                onChange={handleInputChange}
                rows="4"
                placeholder="Terms and conditions..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/purchase-bills')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <FiSave size={18} />
                {isEditMode ? 'Update Purchase Bill' : 'Create Purchase Bill'}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Add Supplier Modal */}
      <AddSupplierModal
        isOpen={showAddSupplierModal}
        onClose={() => setShowAddSupplierModal(false)}
        onSupplierSaved={handleSupplierSaved}
      />

      {/* Edit Supplier Modal */}
      <AddSupplierModal
        isOpen={showEditSupplierModal}
        onClose={() => {
          setShowEditSupplierModal(false);
          setSupplierToEdit(null);
        }}
        onSupplierSaved={handleSupplierUpdated}
        supplier={supplierToEdit}
      />
    </div>
  );
};

export default PurchaseBillForm;
